import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ButtonAlert } from 'components/ButtonAlert';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import type { ThreeForms } from 'components/TotalCount';
import { TotalCount } from 'components/TotalCount';
import { PriceListTableCount } from 'components/TotalCount/PriceListTableCount';
import type { CatalogWorkTypeData } from 'gql/graphql';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { Button, cnMixSpace, Grid, GridItem } from 'uikit';
import { destroyAgGridTableFilters, getCheckedIdsArr } from 'utils';

import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useCatalogListData } from '../CatalogList/hooks';
import { CatalogTablePage } from '../Common/CatalogTablePage';

import { AddOrEditTypeWorkModal } from './AddOrEditTypeWorkModal/AddOrEditTypeWorkModal';
import { CatalogTypesWorkToolbar } from './CatalogTypesWorkToolbar';
import { useCatalogTypesWorkColumns } from './hooks/useCatalogTypesWorkColumns';
import { useCatalogTypesWorkData } from './hooks/useCatalogTypesWorkData';
import { useCatalogWorkTypesUpdatedDate } from './hooks/useCatalogWorkTypesUpdatedDate';
import { useRemoveTypeWork } from './hooks/useRemoveTypeWork';

import Styles from './catalogTypesWork.module.css';

export const rowsWordForms = ['строка', 'строки', 'строк'] as ThreeForms;

export interface IDataEditModal {
    code: string;
    name: string;
    unit: string;
    id?: number;
}

export const CatalogTypesWork = () => {
    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
    const [isCreating, setIsCreating] = useState(false);
    const [isCreateOrEditModalOpened, setIsCreateOrEditModalOpened] = useState(false);
    const [dataEditModal, setDataEditModal] = useState<IDataEditModal | null>(null);
    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});

    const { catalogTypesOfWork: catalogTypesOfWorkRules } = useAccess();

    const { id } = useParams();
    const { tableData } = useCatalogListData();

    const idCatalog = tableData.find((i) => i.id === id)?.idCatalogItem;

    const gridRef = useRef<AgGridReact<CatalogWorkTypeData> | null>(null);

    const catalogPageTitle = 'Справочник видов работ';

    const checkedIdsArr = useMemo(() => getCheckedIdsArr(checkedIds), [checkedIds]);

    const { updatedDate, workTypesDateInProgress, handlerWorkTypesUpdatedDate } = useCatalogWorkTypesUpdatedDate({
        catalogId: idCatalog,
    });

    const refreshData = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.refreshInfiniteCache();
        }
    }, [gridRef]);

    const handleCreateTypeWork = useCallback(() => {
        setIsCreateOrEditModalOpened(true);
        setIsCreating(true);
    }, []);

    const handleClearChecked = useCallback(() => {
        setCheckedIds({});
    }, []);

    const createOrEditModalHandler = useCallback((nextState) => {
        setIsCreateOrEditModalOpened(nextState);
        setIsCreating(false);
    }, []);

    const onRemoveSuccess = useCallback(() => {
        refreshData();
        handlerWorkTypesUpdatedDate();
        setCheckedIds({});
        destroyAgGridTableFilters({
            api: gridRef.current?.api,
            columns: ['code', 'name'],
        });
        setFilterValues({});
    }, [handlerWorkTypesUpdatedDate, refreshData]);

    const { columns } = useCatalogTypesWorkColumns({
        checkedIds,
        handleSetCheckedIds: setCheckedIds,
        createOrEditModalHandler,
        setDataEditModal,
        onRemoveSuccess,
        setFilterValues,
        catalogTypesOfWorkRules,
    });

    const { datasource, totalCount, fetchedCount } = useCatalogTypesWorkData({
        filterValues,
        id,
    });

    const {
        catalogRemoveWorkTypeRequest,
        catalogRemoveWorkTypeStatus,
        catalogRemoveWorkTypeData,
        catalogRemoveWorkTypeError,
        isSubmitted,
        handleCloseModalError,
        handleCreateReport,
    } = useRemoveTypeWork(checkedIdsArr, onRemoveSuccess);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: () => catalogRemoveWorkTypeRequest(),
        });

    return (
        <CatalogTablePage
            catalogName={catalogPageTitle}
            methodology={ProjectMethodologyEnum.Uer}
            isError={false}
            isLoading={false}
        >
            <CatalogTypesWorkToolbar
                handleCreateTypeWork={handleCreateTypeWork}
                updatedDate={updatedDate}
                workTypesDateInProgress={workTypesDateInProgress}
                catalogTypesOfWorkRules={catalogTypesOfWorkRules}
            />
            <div className={Styles.MainWrapper}>
                <Grid cols={2} className={Styles.WrapperCount}>
                    <GridItem>
                        {checkedIdsArr.length ? (
                            <div className={Styles.flexCenter}>
                                <TotalCount
                                    totalCount={checkedIdsArr.length}
                                    forms={rowsWordForms}
                                    prefix={checkedIdsArr.length > 1 ? 'Выбрано' : 'Выбрана'}
                                    className={Styles.TotalCount}
                                />
                                <GridItem>
                                    <ButtonAlert size="xs" label="Удалить" onClick={handleOpenConfirmationDialog} />
                                    <Button
                                        label="Сбросить"
                                        view="ghost"
                                        size="xs"
                                        onClick={handleClearChecked}
                                        className={cnMixSpace({ mL: 's' })}
                                    />
                                </GridItem>
                            </div>
                        ) : (
                            <PriceListTableCount totalCount={fetchedCount} totalItems={totalCount} />
                        )}
                    </GridItem>
                </Grid>
                <div className={Styles.WrapperTable}>
                    <AgGridTable
                        className={Styles.TypesWorkTable}
                        suppressCellFocus
                        rowModelType="infinite"
                        ref={gridRef}
                        columnDefs={columns}
                        maxConcurrentDatasourceRequests={1}
                        maxBlocksInCache={50}
                        cacheBlockSize={50}
                        datasource={datasource}
                    />
                </div>
            </div>
            {isCreateOrEditModalOpened && (
                <AddOrEditTypeWorkModal
                    isModalOpened={isCreateOrEditModalOpened}
                    closeHandler={createOrEditModalHandler}
                    isCreating={isCreating}
                    dataEditModal={dataEditModal}
                    idCatalog={idCatalog}
                    refreshData={refreshData}
                    handlerWorkTypesUpdatedDate={handlerWorkTypesUpdatedDate}
                    gridRefApi={gridRef.current?.api}
                    setFilterValues={setFilterValues}
                />
            )}
            <ConfirmationDialog
                textHeader="Удаление вида работ"
                textConfirmation="Вы уверены, что хотите удалить вид работ?"
                confirmButtonText="Удалить"
                cancelButtonText="Не удалять"
                onSubmit={handleActionQuery}
                isOpened={isOpenConfirmationDialog}
                onClose={handleCloseConfirmationDialog}
            />
            <ErrorHandlingModal
                titleText="Удаление видов работ"
                customButtonText="Понятно"
                viewButton="primary"
                isSubmitted={isSubmitted}
                statusRequest={catalogRemoveWorkTypeStatus}
                dataRequest={catalogRemoveWorkTypeData?.catalogRemoveWorkTypes}
                errorsRequest={catalogRemoveWorkTypeError}
                handleCloseModalError={handleCloseModalError}
                handleCreateReport={handleCreateReport}
            />
        </CatalogTablePage>
    );
};

CatalogTypesWork.displayName = 'CatalogTypesWork';
